import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  OutlinedInput,
  TextField,
  Button,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Tooltip,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { useGetOrganizationBrandQuery } from '../features/api/rest/apiBrandOrganizationSlice';
import {
  useGetOrganizationProductQuery,
  useCreateOrganizationProductMutation,
  useAddProductBrandMutation,
  useDestroyProductBrandMutation,
  useEditOneOrganizationProductMutation,
  usePatchOneOrganizationProductMutation,
  useGetProductBrandQuery,
} from '../features/api/rest/apiProductOrganizationSlice';
import BrandOrganizationDialog from './BrandOrganizationDialog';
import Error from './Error';
import Spinner from './Spinner';

function ProductOrganizationDialog({ actionType, isOpen, setIsOpen, oldName = '', oldEan = '' }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  let title = '';
  let handleConfirm = () => {};
  const [dialogFullscreen, setDialogFullscreen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [dialogName, setDialogName] = useState(oldName || '');
  const [nameError, setNameError] = useState({
    error: false,
    text: '',
  });
  const [eanError, setEanError] = useState({
    error: false,
    text: '',
  });
  const [dialogEan, setDialogEan] = useState('');
  const [dialogBrands, setDialogBrands] = useState([]);
  const [brandsError, setBrandsError] = useState({
    error: false,
    text: '',
  });
  const [brandsItems, setBrandsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addProduct] = useCreateOrganizationProductMutation();
  const [addProductBrand] = useAddProductBrandMutation();
  const [deleteProductBrand] = useDestroyProductBrandMutation();
  const [editProduct] = useEditOneOrganizationProductMutation();
  const [patchProduct] = usePatchOneOrganizationProductMutation();
  const { products } = useGetOrganizationProductQuery(
    { opk },
    {
      selectFromResult: ({ data }) => ({
        products: data?.results,
      }),
    }
  );
  const { brands, isLoading, isFetching, isError } = useGetOrganizationBrandQuery(
    { opk },
    {
      selectFromResult: ({ data }) => ({
        brands: data?.results,
      }),
    }
  );
  const { productBrands } =
    actionType === 'edit' && oldEan
      ? useGetProductBrandQuery(
          { opk, ean: oldEan },
          {
            selectFromResult: ({ data }) => ({
              productBrands: data?.results,
            }),
          }
        )
      : { productBrands: [] };

  useEffect(() => {
    if (isOpen) {
      setDialogFullscreen(false);
      setDialogName(oldName || '');
      setNameError({
        error: false,
        text: '',
      });
      setDialogEan(oldEan || '');
      setEanError({
        error: false,
        text: '',
      });
      setDialogBrands([]);
      setBrandsError({
        error: false,
        text: '',
      });
      setBrandsItems(productBrands || []);
      setLoading(false);
    }
  }, [isOpen]);
  const validation = () => {
    if (dialogName === '') {
      setNameError({
        text: t('organizations.product.dialog.error.name'),
        error: true,
      });
      return false;
    }
    if (dialogEan === '') {
      setEanError({
        text: t('organizations.product.dialog.error.ean'),
        error: true,
      });
      return false;
    }
    if (actionType === 'edit') {
      if (products && products.some((product) => product.name === dialogName && product.ean_code !== oldEan)) {
        setNameError({
          text: t('organizations.product.dialog.error.nameExists'),
          error: true,
        });
        return false;
      }
      if (products && products.some((product) => product.ean_code === dialogEan && product.ean_code !== oldEan)) {
        setNameError({
          text: t('organizations.product.dialog.error.eanExists'),
          error: true,
        });
        return false;
      }
    } else {
      if (products && products.some((product) => product.name === dialogName)) {
        setEanError({
          text: t('organizations.product.dialog.error.nameExists'),
          error: true,
        });
        return false;
      }
      if (products && products.some((product) => product.ean_code === dialogEan)) {
        setEanError({
          text: t('organizations.product.dialog.error.eanExists'),
          error: true,
        });
        return false;
      }
    }
    return true;
  };
  const handleAdd = async () => {
    if (!validation()) return;
    setLoading(true);
    try {
      const response = await addProduct({ opk, payload: { ean_code: dialogEan, name: dialogName } });
      if (response.error) {
        throw new Error(response.error);
      }
      const errors = [];
      await Promise.all(
        brandsItems
          .filter((brandItem) => brandItem.name !== '')
          .map(async (brandItem) => {
            if (brandItem.name !== '') {
              const r = addProductBrand({ opk, ean: response.data.ean_code, payload: { id: brandItem.id } });
              if (r.error) {
                errors.push(r.error);
              }
            }
          })
      );
      if (errors.length > 0) {
        throw new Error();
      }
      setIsOpen(false);
    } catch (error) {
      setNameError({
        text: t('organizations.product.dialog.error.generic'),
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = async () => {
    if (!validation()) return;
    setLoading(true);
    try {
      let response = { error: false };
      if (dialogName !== oldName && dialogEan !== oldEan) {
        response = await editProduct({ opk, ean: oldEan, payload: { ean_code: dialogEan, name: dialogName } });
      } else if (dialogName !== oldName) {
        response = await patchProduct({ opk, ean: oldEan, payload: { name: dialogName } });
      } else if (dialogEan !== oldEan) {
        response = await patchProduct({ opk, ean: oldEan, payload: { ean_code: dialogEan } });
      }
      if (response.error) {
        throw new Error(response.error);
      }
      const updatedEan = dialogEan !== oldEan ? response.data.ean_code : oldEan;
      const errors = [];
      await Promise.all(
        brandsItems.map(async (brandItem, index) => {
          if (brandItem.name === '' && index < productBrands.length) {
            const r = deleteProductBrand({ opk, ean: updatedEan, id: brandItem.id });
            if (r.error) {
              errors.push(r.error);
            }
          }
        })
      );
      await Promise.all(
        brandsItems.slice(productBrands.length).map(async (brandItem) => {
          if (brandItem.name !== '') {
            const r = addProductBrand({ opk, ean: updatedEan, payload: { id: brandItem.id } });
            if (r.error) {
              errors.push(r.error);
            }
          }
        })
      );
      if (errors.length > 0) {
        throw new Error();
      }
      setIsOpen(false);
    } catch (error) {
      setNameError({
        text: t('organizations.product.dialog.error.generic'),
        error: true,
      });
      setEanError({
        text: t('organizations.product.dialog.error.generic'),
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDialogNameChange = (e) => {
    setDialogName(e.target.value);
    if (nameError.error) {
      setNameError({
        ...nameError,
        error: false,
      });
    }
  };
  const handleDialogEanChange = (e) => {
    setDialogEan(e.target.value);
    if (eanError.error) {
      setEanError({
        ...eanError,
        error: false,
      });
    }
  };
  const handleDialogBrandsChange = (e) => {
    setBrandsError({
      error: false,
      text: '',
    });
    const selected = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    setDialogBrands(selected.filter((value) => value !== ''));
  };
  const handleAddBrand = () => {
    if (dialogBrands.length === 0) {
      setBrandsError({
        error: true,
        text: t('organizations.product.dialog.error.brand'),
      });
      return;
    }
    setBrandsItems(brandsItems.concat(dialogBrands.map((brand) => brands.find((item) => item.id === brand))));
    setDialogBrands([]);
  };
  const handleRemoveBrand = (index) => {
    const updatedBrandsItems = [...brandsItems];
    updatedBrandsItems[index] = {
      ...updatedBrandsItems[index],
      name: '',
    };
    setBrandsItems(updatedBrandsItems);
  };
  switch (actionType) {
    case 'add':
      title = t('organizations.product.add');
      handleConfirm = handleAdd;
      break;
    case 'edit':
      title = t('organizations.product.edit');
      handleConfirm = handleEdit;
      break;
    default:
      title = '';
  }

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  return (
    <Dialog fullWidth open={isOpen} onClose={() => setIsOpen(false)} fullScreen={dialogFullscreen}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Tooltip title={dialogFullscreen ? t('organizations.product.dialog.exitFullscreen') : t('organizations.product.dialog.fullscreen')} placement="right">
          <IconButton onClick={() => setDialogFullscreen(!dialogFullscreen)} size="large" sx={{ position: 'absolute', top: 8, left: 8 }}>
            {dialogFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {title}
      </DialogTitle>
      <DialogContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TextField
          fullWidth
          required
          value={dialogName}
          id="productname"
          sx={{ mt: 2 }}
          error={nameError.error}
          helperText={nameError.error ? nameError.text : ''}
          label={t('organizations.product.dialog.name')}
          name="productname"
          inputProps={{ 'product-data': 'name' }}
          onChange={handleDialogNameChange}
        />
        <TextField
          fullWidth
          required
          value={dialogEan}
          id="productean"
          sx={{ mt: 2 }}
          error={eanError.error}
          helperText={eanError.error ? eanError.text : ''}
          label={t('organizations.product.dialog.ean')}
          name="productean"
          inputProps={{ 'product-data': 'ean' }}
          onChange={handleDialogEanChange}
        />
        {brandsItems.length > 0 && (
          <Typography variant="h6" sx={{ mt: 3 }}>
            {t('organizations.brand.brands')}
          </Typography>
        )}
        <List sx={{ overflowY: 'auto' }}>
          {brandsItems &&
            brandsItems.map(
              (item, index) =>
                item.name !== '' && (
                  <ListItem key={item.id}>
                    <Grid container alignItems="center">
                      <Grid item xs>
                        <ListItemText
                          primary={item.name}
                          sx={{
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: '3',
                            WebkitBoxOrient: 'vertical',
                            textAlign: 'initial',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Tooltip title={t('common.actions.delete')} placement="top">
                          <IconButton onClick={() => handleRemoveBrand(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </ListItem>
                )
            )}
        </List>
        <Grid container spacing={1} display="flex" alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs>
            <FormControl fullWidth error={brandsError.error}>
              <InputLabel>{t('organizations.product.dialog.brand')}</InputLabel>
              <Select
                multiple
                value={dialogBrands}
                onChange={handleDialogBrandsChange}
                label={t('organizations.product.dialog.brand')}
                input={<OutlinedInput label={t('organizations.product.dialog.brand')} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={brands.find((brand) => brand.id === value)?.name} />
                    ))}
                  </Box>
                )}
              >
                <MenuItem onClick={() => setIsAdding(true)} value="">
                  <Typography sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <AddIcon />
                    {t('organizations.brand.add')}
                  </Typography>
                </MenuItem>
                <Divider />
                {brands &&
                  brands.map((brand) => (
                    <MenuItem disabled={!!brandsItems.find((item) => item.id === brand.id && item.name !== '')} key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>{brandsError.error ? brandsError.text : ''}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item>
            <Tooltip title={t('organizations.brand.add')} placement="top">
              <IconButton onClick={handleAddBrand} size="large">
                <AddIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      {loading ? (
        <Grid container display="flex" justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : null}
      <DialogActions sx={{ p: 3 }}>
        <Grid container display="flex" justifyContent="flex-end" gap={1}>
          <Grid item>
            <Button type="submit" variant="outlined" color="secondary" onClick={() => setIsOpen(false)}>
              {t('common.actions.close')}
            </Button>
          </Grid>
          <Grid item>
            <Button disabled={loading} type="submit" variant="contained" color="primary" onClick={handleConfirm}>
              {t('common.actions.save')}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
      <BrandOrganizationDialog actionType="add" isOpen={isAdding} setIsOpen={setIsAdding} />
    </Dialog>
  );
}

ProductOrganizationDialog.propTypes = {
  actionType: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  oldName: PropTypes.string,
  oldEan: PropTypes.string,
};

export default ProductOrganizationDialog;
